// eslint-disable-next-line
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import '../css/logging.css';

import { AppContext } from '../AppContext';
import Container from '../basicComponents/Container';
import Text from '../basicComponents/Text';
import { Logging, LoggingResponse } from '../data/Logging';
import LogRecord from '../data/LogRecord';
import { RecordNavigation } from '../App';
import SessionHeader from '../appComponents/SessionHeader';
import TextClick from '../basicComponents/TextClick';
import RadioToggleButtons from '../basicComponents/RadioToggleButtons';

export interface LoggingPageProps extends RouteComponentProps { 
    curRecord: LogRecord | undefined;
    recordNavigation: RecordNavigation;
    setCurRecord: (record: LogRecord) => void;
}

export interface LoggingPageState { 
    entries: Logging[] | undefined;
    viewLevel: number;
    selectedLevel: string;
}

class LoggingPageComponent extends React.Component<LoggingPageProps, LoggingPageState> {

    // app context
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    private sessionId: string;

    private recordNavigation: RecordNavigation;

    constructor(props: LoggingPageProps) {

        super(props);

        const path = this.props.history.location.pathname.split("/");
        this.sessionId = path[path.length - 1];

        this.recordNavigation = { 
            ...this.props.recordNavigation,
            moveToRecord: this.moveToRecord,
        };

        this.state = {
            entries: undefined,
            viewLevel: 0,
            selectedLevel: 'Fine'
        };

    }

    public shouldComponentUpdate(): boolean {

        console.log("shouldComponentUpdate called");
        const path = this.props.history.location.pathname.split("/");
        const curSessionId = path[path.length - 1];

        if (this.sessionId !== curSessionId) {

            this.sessionId = curSessionId;
            this.setState({ entries: undefined });

            // get logging
            this.context.restAppService.request_get('logging/record/' + this.sessionId, this.context.user.getToken(),
                (data): void => {
                    const response = JSON.parse(data) as LoggingResponse;
                    this.setState({ entries: response.entries });
                },
                (status, text): void => {
                    console.log(`request failure ${status} ${text}`)
                }
            );

        }
        return true;
    }

    public render(): JSX.Element {

        if (this.state.entries === undefined) {
            console.log('logging  no entries found')
            return <div />
        }

		if (this.props.curRecord === undefined) {
            console.log('logging  no curRecord found')
			return <div />
		}
        
        return (
            <Container className='logging-page'>
                <Container className='session-line'>
                    <SessionHeader curRecord={this.props.curRecord} recordNavigation={this.recordNavigation} />
                    <div className='session-space' />
                    <RadioToggleButtons entries={['Fine', 'Info', 'IFace', 'Warning', 'Severe']} selectedOption={this.state.selectedLevel} onChange={this.handleButtonChange}></RadioToggleButtons>
                    <TextClick className='session-button-group' name='dialog' onClick={this.handleButtonClick}>Dialog</TextClick>
                    <TextClick className='session-button' name='history' onClick={this.handleButtonClick}>History</TextClick>
                </Container>
                <Container className='logging-list'>
                    {this.state.entries
                        .filter( entry => entry.level >= this.state.viewLevel )
                        .map( (entry, idx) => {

                            entry.logDate = entry.logDate.replace('T', ' ');

                            let levelClass = 'logging-levelFine';
                            if (entry.level >= 1000) { levelClass = 'logging-levelSevere'; }
                            else if (entry.level >= 900) { levelClass = 'logging-levelWarning'; }
                            else if (entry.level >= 850) { levelClass = 'logging-levelIFace'; }
                            else if (entry.level >= 800) { levelClass = 'logging-levelInfo'; }

                            return (
                                <React.Fragment key={idx}>
                                    <Text className={levelClass}></Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.logDate}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.logTime}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.thread}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.level}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.className}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.method}</Text>
                                    <Text className={idx % 2 === 0 ? ' logging-value-even' : ' logging-value-odd'}>{entry.message}</Text>
                                </React.Fragment> 
                            );

                    })}
                </Container>
            </Container>
            
        );

    }

    public componentDidMount(): void {

        // get log record if necessary
        if (this.props.curRecord === undefined) {

            this.context.restAppService.request_get('logRecord/request/' + this.sessionId, this.context.user.getToken(),
                (data): void => {

                    // get response
                    const response = JSON.parse(data) as LogRecord;

                    // set curRecord
                    this.props.setCurRecord(response);

                    // set sidebar
                    this.context.app.addSidebar({ name: 'LOG ' + this.sessionId, link: '/logging/' + this.sessionId });      

                },
                (status, text) => {
                    console.log(`request failure ${status} ${text}`)
                }
            );
        }

        // get logging
        this.context.restAppService.request_get('logging/record/' + this.sessionId, this.context.user.getToken(),
            (data): void => {
                const response = JSON.parse(data) as LoggingResponse;
                this.setState({ entries: response.entries });
            },
            (status, text): void => {
                console.log(`request failure ${status} ${text}`)
            }
        );

    }

    public moveToRecord = (nextRecord: LogRecord): void => {

        if (this.props.curRecord) {

            // replace entry in sidebar
            this.context.app.replaceSidebar(
                { name: 'LOG ' + this.props.curRecord.sessionId, link: '/logging/' + this.props.curRecord.sessionId }, 
                { name: 'LOG ' + nextRecord.sessionId, link: '/logging/' + nextRecord.sessionId }
                );

            // navigate to new index
            this.props.setCurRecord(nextRecord);
            this.props.history.push('/logging/' + nextRecord.sessionId);

        }

    }

    private handleButtonClick = (event: React.MouseEvent<HTMLSpanElement>): void => {

        const target = event.currentTarget;
        const name = target.dataset.name;

        switch (name) {

            case 'dialog':
                this.context.app.addSidebar({ name: 'DLG ' + this.sessionId, link: '/dialog/' + this.sessionId });
                this.props.history.push('/dialog/' + this.sessionId);
                break;
            case 'history': 
                this.context.app.addSidebar({ name: 'HTY ' + this.sessionId, link: '/logHistory/' + this.sessionId });
                this.props.history.push('/logHistory/' + this.sessionId);
                break;

        }

    }

    private handleButtonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {

        const target = event.currentTarget;

        switch (target.id) {

            case 'optionFine': this.setState({ viewLevel: 500, selectedLevel: 'Fine' }); break;
            case 'optionInfo': this.setState({ viewLevel: 800, selectedLevel: 'Info' }); break;
            case 'optionIFace': this.setState({ viewLevel: 850, selectedLevel: 'IFace' }); break;
            case 'optionWarning': this.setState({ viewLevel: 900, selectedLevel: 'Warning' }); break;
            case 'optionSevere': this.setState({ viewLevel: 1000, selectedLevel: 'Severe' }); break;

        }

    }

}

const LoggingPage = withRouter(LoggingPageComponent);
export default LoggingPage;
